/**
 * Dialog for saving desktop with "Save As" functionality
 * Shows input field for new name + list of existing desktops to overwrite
 */

import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { confirmDialog } from "primereact/confirmdialog";
import { Tooltip } from "primereact/tooltip";
import PropTypes from "prop-types";
import ModalBase from "../../components/Modals/ModalBase";
import "./DesktopManager.css";

const SaveAsDesktopDialog = ({ visible, onHide, onSave, onRefresh }) => {
  const [desktops, setDesktops] = useState([]);
  const [desktopName, setDesktopName] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Load desktops when dialog opens
  useEffect(() => {
    if (visible) {
      setDesktopName("");
      setError("");
      loadDesktops();
    }
  }, [visible]);

  const loadDesktops = async () => {
    setLoading(true);
    setError("");

    try {
      const list = await onRefresh();
      setDesktops(list || []);
    } catch (err) {
      console.error("Error loading desktops:", err);
      setError(err.message || "Falha ao carregar lista de desktops");
      setDesktops([]);
    } finally {
      setLoading(false);
    }
  };

  const validateName = (name) => {
    if (!name || name.trim() === "") {
      return "Nome do desktop é obrigatório";
    }
    if (name.trim().length < 3) {
      return "Nome deve ter pelo menos 3 caracteres";
    }
    if (name.trim().length > 50) {
      return "Nome deve ter no máximo 50 caracteres";
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validateName(desktopName);
    if (validationError) {
      setError(validationError);
      return;
    }

    const trimmedName = desktopName.trim();

    // Check if name already exists
    const existingDesktop = desktops.find(
      (d) => d.name.toLowerCase() === trimmedName.toLowerCase(),
    );

    if (existingDesktop) {
      // Ask for confirmation to overwrite
      confirmDialog({
        message: `Já existe um desktop com o nome "${trimmedName}". Deseja sobrescrever?`,
        header: "Confirmar Sobrescrita",
        icon: "pi pi-exclamation-triangle",
        acceptClassName: "p-button-warning",
        acceptLabel: "Sobrescrever",
        rejectLabel: "Cancelar",
        accept: async () => {
          await performSave(trimmedName, existingDesktop.id);
        },
      });
    } else {
      // New name - save directly
      await performSave(trimmedName);
    }
  };

  const performSave = async (name, existingId = null) => {
    setSaving(true);
    setError("");

    try {
      await onSave(name, existingId);
      onHide(); // Close dialog on success
    } catch (err) {
      console.error("Error saving desktop:", err);
      setError(err.message || "Falha ao salvar desktop");
    } finally {
      setSaving(false);
    }
  };

  const handleOverwrite = (desktop) => {
    // Fill the input with the desktop name
    setDesktopName(desktop.name);

    // Show confirmation
    confirmDialog({
      message: `Deseja sobrescrever o desktop "${desktop.name}"?`,
      header: "Confirmar Sobrescrita",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-warning",
      acceptLabel: "Sobrescrever",
      rejectLabel: "Cancelar",
      accept: async () => {
        await performSave(desktop.name, desktop.id);
      },
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !saving) {
      handleSave();
    }
  };

  const formatDate = (timestamp) => {
    try {
      // Check if timestamp is Unix timestamp (seconds since epoch)
      // If it's a number less than year 2100 in milliseconds, it's likely in seconds
      let date;
      if (typeof timestamp === "number" && timestamp < 4102444800000) {
        // Convert from seconds to milliseconds
        date = new Date(timestamp * 1000);
      } else {
        date = new Date(timestamp);
      }

      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return timestamp;
    }
  };

  // Column templates
  const nameTemplate = (rowData) => {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
        <i
          className="pi pi-desktop"
          style={{ fontSize: "12px", color: "var(--primary-color)" }}
        ></i>
        <span
          style={{
            fontSize: "12px",
            fontWeight: "500",
            color: "var(--text-color)",
          }}
        >
          {rowData.name}
        </span>
      </div>
    );
  };

  const dateTemplate = (rowData) => {
    return (
      <span
        style={{
          fontSize: "11px",
          color: "var(--text-color-secondary)",
        }}
      >
        {formatDate(rowData.timestamp)}
      </span>
    );
  };

  const actionsTemplate = (rowData) => {
    return (
      <div style={{ display: "flex", gap: "1px", justifyContent: "center" }}>
        <Button
          icon="pi pi-save"
          className="p-button-text p-button-sm p-button-warning desktop-action-btn-overwrite"
          onClick={() => handleOverwrite(rowData)}
          data-pr-tooltip="Sobrescrever"
          data-pr-position="top"
          style={{ fontSize: "11px", padding: "4px" }}
          data-evo-id={`saveasdesktopdialog-btn-overwrite-${rowData.id}`}
        />
      </div>
    );
  };

  const headerWithSearch = (
    <div className="desktop-dialog-header-inline">
      <span className="desktop-dialog-title">Salvar Desktop Como</span>
      <div className="desktop-header-search">
        <i className="pi pi-search" />
        <input
          type="text"
          autoComplete="off"
          className="p-inputtext p-component"
          placeholder="Buscar..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          data-evo-id="saveasdesktopdialog-input-filter"
        />
      </div>
      <Button
        icon="pi pi-refresh"
        className="p-button-text p-button-sm p-button-rounded desktop-refresh-btn"
        onClick={loadDesktops}
        disabled={loading}
        data-pr-tooltip="Atualizar lista"
        data-pr-position="top"
        style={{ width: "26px", height: "26px", padding: "0" }}
        data-evo-id="saveasdesktopdialog-btn-refresh"
      />
    </div>
  );

  const emptyMessage = (
    <div className="desktop-empty-state">
      <i className="pi pi-inbox"></i>
      <p>
        {globalFilter
          ? "Nenhum desktop encontrado com esse nome"
          : "Nenhum desktop salvo encontrado"}
      </p>
    </div>
  );

  return (
    <>
      <Tooltip target=".desktop-action-btn-overwrite" />
      <Tooltip target=".desktop-refresh-btn" />

      <ModalBase
        header={headerWithSearch}
        visible={visible}
        onHide={onHide}
        size="large"
        variant="data"
        footer={null}
        closable={!saving}
        className="desktop-load-dialog-modal save-as-desktop-dialog"
      >
        <div className="desktop-load-dialog">
          {/* Name Input Section */}
          <div
            className="save-as-name-section"
            style={{
              padding: "0.375rem 0.5rem",
            }}
          >
            <label
              htmlFor="desktop-name-input"
              style={{
                display: "block",
                marginBottom: "0.375rem",
                fontWeight: "600",
                fontSize: "12px",
              }}
            >
              Nome do Desktop
            </label>
            <div
              className="save-as-input-row"
              style={{
                display: "flex",
                gap: "0.375rem",
                alignItems: "flex-start",
              }}
            >
              <div style={{ flex: 1 }}>
                <InputText
                  id="desktop-name-input"
                  value={desktopName}
                  onChange={(e) => setDesktopName(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Nome do novo desktop"
                  autoFocus
                  disabled={saving}
                  style={{ width: "100%", fontSize: "0.8125rem" }}
                  data-evo-id="saveasdesktopdialog-input-name"
                />
                <small
                  style={{
                    display: "block",
                    marginTop: "0.125rem",
                    color: "var(--text-color-secondary)",
                    fontSize: "10px",
                  }}
                >
                  Ou selecione um existente abaixo para sobrescrever
                </small>
              </div>
              <Button
                label="Salvar"
                icon="pi pi-check"
                onClick={handleSave}
                disabled={saving || !desktopName.trim()}
                loading={saving}
                style={{ fontSize: "0.8125rem" }}
                data-evo-id="saveasdesktopdialog-btn-save"
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <Message
              severity="error"
              text={error}
              className="desktop-error-message"
            />
          )}

          {/* Desktop Table */}
          {loading ? (
            <div className="desktop-loading-state">
              <ProgressSpinner style={{ width: "50px", height: "50px" }} />
              <p>Carregando desktops...</p>
            </div>
          ) : (
            <DataTable
              value={desktops}
              globalFilter={globalFilter}
              emptyMessage={emptyMessage}
              scrollable
              scrollHeight="300px"
              showGridlines
              stripedRows
              size="small"
              className="desktop-table"
            >
              <Column
                field="name"
                header="Nome"
                body={nameTemplate}
                sortable
                headerClassName="desktop-col-name"
                bodyClassName="desktop-col-name"
              />
              <Column
                field="timestamp"
                header="Data"
                body={dateTemplate}
                sortable
                style={{ width: "130px" }}
                headerClassName="desktop-col-date"
                bodyClassName="desktop-col-date"
              />
              <Column
                header="Ações"
                body={actionsTemplate}
                style={{ width: "80px", textAlign: "center" }}
                headerClassName="desktop-col-actions"
                bodyClassName="desktop-col-actions"
              />
            </DataTable>
          )}
        </div>
      </ModalBase>
    </>
  );
};

SaveAsDesktopDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
};

export default SaveAsDesktopDialog;
